import { graphqlQuery } from "@/lib/api/graphql";
import {
	buildGraphQLSortVariable as buildSort,
	computeMockPagination,
	computePaginationWithCount,
	createDefaultPagination,
	type GraphQLConnection,
	type PaginationResult,
	pageToCursor,
} from "@/lib/graphql-pagination";
import { useApiConfig } from "@/providers/ApiConfigProvider";
import type {
	Transaction,
	TransactionStatus,
	TransactionType,
} from "@/types/transaction";
import { useModeAwareQuery } from "./useModeAwareQuery";

// --- GraphQL node type for add promise transactions ---

interface AddPromiseTransactionGraphQLNode {
	transactionId: number;
	referenceNumber: string | null;
	customerId: number;
	customerName: string | null;
	accountNumber: string | null;
	transactionDate: string;
	dueDate: string | null;
	amount: number;
	netAmount: number | null;
	vatAmount: number | null;
	outstandingAmount: number;
	description: string | null;
	transactionNotes: string | null;
	headerReference: string | null;
	accountCode: string | null;
	onDispute: boolean;
	hasDispute: boolean;
	subLedger: string | null;
	subLedgerName: string | null;
	orderNumber: string | null;
	documentUrl: string | null;
	transactionReference: string | null;
	bankReference: string | null;
	currencyAmount: number | null;
	currencyNetAmount: number | null;
	currencyVatAmount: number | null;
	currencyOutstandingAmount: number | null;
	displayAmount: number | null;
	displayNetAmount: number | null;
	displayVatAmount: number | null;
	displayOutstandingAmount: number | null;
	daysOverdue: number;
	transactionCreatedAt: string;
	transactionModifiedAt: string | null;
	currencyCode: string | null;
	transactionTypeCode: string | null;
	transactionTypeName: string | null;
	transactionStatusCode: string | null;
	transactionStatusName: string | null;
	paymentMethodName: string | null;
	createdByName: string | null;
	modifiedByName: string | null;
	ageingBucket: string | null;
	totalAllocated: number;
	allocationCount: number;
	allocationRef: string | null;
	allocationStatusCode: string | null;
	allocationStatusName: string | null;
	hasPromise: boolean;
	hasCase: boolean;
}

interface AddPromiseTransactionListGraphQLResponse {
	addPromiseTransactions: {
		addPromiseTransactionList: GraphQLConnection<AddPromiseTransactionGraphQLNode>;
	};
}

interface AddPromiseTransactionCountGraphQLResponse {
	addPromiseTransactions: {
		addPromiseTransactionCount: number;
	};
}

// --- GraphQL queries ---

const ADD_PROMISE_TRANSACTION_LIST_QUERY = `
  query AddPromiseTransactionList($first: Int, $after: String, $sort: [AddPromiseTransactionListSortSortByInput!], $filter: AddPromiseTransactionListFilterInput) {
    addPromiseTransactions {
      addPromiseTransactionList(first: $first, after: $after, sort: $sort, filter: $filter) {
        edges {
          node {
            transactionId
            referenceNumber
            customerId
            customerName
            accountNumber
            transactionDate
            dueDate
            amount
            netAmount
            vatAmount
            outstandingAmount
            description
            transactionNotes
            headerReference
            accountCode
            onDispute
            hasDispute
            subLedger
            subLedgerName
            orderNumber
            documentUrl
            transactionReference
            bankReference
            currencyAmount
            currencyNetAmount
            currencyVatAmount
            currencyOutstandingAmount
            displayAmount
            displayNetAmount
            displayVatAmount
            displayOutstandingAmount
            daysOverdue
            transactionCreatedAt
            transactionModifiedAt
            currencyCode
            transactionTypeCode
            transactionTypeName
            transactionStatusCode
            transactionStatusName
            paymentMethodName
            createdByName
            modifiedByName
            ageingBucket
            totalAllocated
            allocationCount
            allocationRef
            allocationStatusCode
            allocationStatusName
            hasPromise
            hasCase
          }
        }
        pageInfo {
          startCursor
          endCursor
          hasNextPage
          hasPreviousPage
        }
      }
    }
  }
`;

const ADD_PROMISE_TRANSACTION_COUNT_QUERY = `
  query AddPromiseTransactionCount($filter: AddPromiseTransactionListFilterInput) {
    addPromiseTransactions {
      addPromiseTransactionCount(filter: $filter)
    }
  }
`;

// --- Sort field map ---

const addPromiseSortFieldMap: Record<string, string> = {
	referenceNumber: "ReferenceNumber",
	transactionDate: "TransactionDate",
	dueDate: "DueDate",
	customerName: "CustomerName",
	transactionType: "TransactionTypeName",
	amount: "Amount",
	outstandingAmount: "OutstandingAmount",
	status: "TransactionStatusCode",
	currency: "CurrencyCode",
	description: "Description",
	createdBy: "CreatedByName",
	modifiedBy: "ModifiedByName",
	accountCode: "AccountCode",
	netAmount: "NetAmount",
	vatAmount: "VatAmount",
	onDispute: "OnDispute",
	subLedger: "SubLedger",
	orderNumber: "OrderNumber",
	currencyGross: "CurrencyAmount",
	currencyNet: "CurrencyNetAmount",
	currencyVAT: "CurrencyVatAmount",
	currencyOutstanding: "CurrencyOutstandingAmount",
};

// --- Mapping ---

function mapTransactionTypeCode(code: string | null): TransactionType {
	const typeMap: Record<string, TransactionType> = {
		invoice: "invoice",
		credit_note: "credit_note",
		receipt: "receipt",
		debit_adjustment: "debit_adjustment",
		credit_adjustment: "credit_adjustment",
	};
	return typeMap[(code ?? "").toLowerCase()] ?? "invoice";
}

function mapTransactionStatusCode(code: string | null): TransactionStatus {
	const statusMap: Record<string, TransactionStatus> = {
		open: "outstanding",
		paid: "completed",
		overdue: "overdue",
	};
	return statusMap[(code ?? "").toLowerCase()] ?? "outstanding";
}

function reverseMapTransactionStatus(frontendStatus: string): string {
	const reverseMap: Record<string, string> = {
		outstanding: "Open",
		completed: "Paid",
		overdue: "Overdue",
	};
	return reverseMap[frontendStatus] ?? frontendStatus;
}

function mapGraphQLNodeToTransaction(
	node: AddPromiseTransactionGraphQLNode,
): Transaction {
	return {
		id: node.transactionId,
		referenceNumber: node.referenceNumber || String(node.transactionId),
		transactionDate: node.transactionDate,
		customerId: node.customerId,
		customerName: node.customerName ?? "",
		transactionType: mapTransactionTypeCode(node.transactionTypeCode),
		amount: node.amount,
		currency: node.currencyCode ?? "GBP",
		status: mapTransactionStatusCode(node.transactionStatusCode),
		description: node.description ?? "",
		notes: node.transactionNotes ?? undefined,
		createdBy: node.createdByName ?? "",
		modifiedBy: node.modifiedByName ?? undefined,
		createdAt: node.transactionCreatedAt,
		modifiedAt: node.transactionModifiedAt ?? undefined,
		dueDate: node.dueDate ?? undefined,
		headerReference: node.headerReference ?? undefined,
		paymentMethod: node.paymentMethodName ?? undefined,
		documentUrl: node.documentUrl ?? undefined,
		outstandingAmount: node.outstandingAmount,
		onDispute: node.onDispute,
		hasDispute: node.hasDispute,
		accountCode: node.accountCode ?? undefined,
		netAmount: node.netAmount ?? undefined,
		vatAmount: node.vatAmount ?? undefined,
		subLedger: node.subLedger ?? undefined,
		subLedgerName: node.subLedgerName ?? undefined,
		orderNumber: node.orderNumber ?? undefined,
		transactionReference: node.transactionReference ?? undefined,
		bankReference: node.bankReference ?? undefined,
		currencyGross: node.currencyAmount ?? undefined,
		currencyNet: node.currencyNetAmount ?? undefined,
		currencyVAT: node.currencyVatAmount ?? undefined,
		currencyOutstanding: node.currencyOutstandingAmount ?? undefined,
		displayAmount: node.displayAmount ?? undefined,
		displayNetAmount: node.displayNetAmount ?? undefined,
		displayVatAmount: node.displayVatAmount ?? undefined,
		displayOutstandingAmount: node.displayOutstandingAmount ?? undefined,
		accountNumber: node.accountNumber ?? undefined,
		daysOverdue: node.daysOverdue,
		ageingBucket: node.ageingBucket ?? "",
		hasPromise: node.hasPromise,
		hasCase: node.hasCase,
		allocationStatusCode: node.allocationStatusCode ?? "",
		allocationStatusName: node.allocationStatusName ?? "",
		totalAllocated: node.totalAllocated,
		allocationCount: node.allocationCount,
		allocationRef: node.allocationRef ?? undefined,
	};
}

// --- Filter helpers ---

function buildRangeFilter<T>(
	min: T | undefined,
	max: T | undefined,
): Record<string, T> | undefined {
	if (min === undefined && max === undefined) return undefined;
	const range: Record<string, T> = {};
	if (min !== undefined) range.ge = min;
	if (max !== undefined) range.le = max;
	return range;
}

function buildContainsFilter(
	values: string[] | undefined,
): { containsValues: string[] } | undefined {
	return values && values.length > 0
		? { containsValues: values }
		: undefined;
}

// --- Filter builder ---

export interface GetAddPromiseTransactionsParams {
	page?: number;
	pageSize?: number;
	search?: string;
	sortBy?: string;
	sortOrder?: "asc" | "desc";
	transactionTypes?: string[];
	statuses?: string[];
	currencies?: string[];
	dateFrom?: string;
	dateTo?: string;
	amountMin?: number;
	amountMax?: number;
	outstandingBalanceMin?: number;
	outstandingBalanceMax?: number;
	onDispute?: boolean;
	hasCase?: boolean;
}

function buildAddPromiseTransactionFilter(
	customerId: number | undefined,
	excludePromiseId: number | undefined,
	params?: GetAddPromiseTransactionsParams,
): Record<string, unknown> | undefined {
	const filter: Record<string, unknown> = {};

	if (customerId) {
		filter.customerId = { eq: customerId };
	}

	if (excludePromiseId) {
		filter.excludePromiseId = excludePromiseId;
	}

	if (params?.search) {
		filter.summary = params.search;
	}

	const typeFilter = buildContainsFilter(params?.transactionTypes);
	if (typeFilter) filter.transactionTypeCode = typeFilter;

	const statusFilter = buildContainsFilter(
		params?.statuses?.map(reverseMapTransactionStatus),
	);
	if (statusFilter) filter.transactionStatusCode = statusFilter;

	const currencyFilter = buildContainsFilter(params?.currencies);
	if (currencyFilter) filter.currencyCode = currencyFilter;

	const dateRange = buildRangeFilter(params?.dateFrom, params?.dateTo);
	if (dateRange) filter.transactionDate = dateRange;

	const amountRange = buildRangeFilter(params?.amountMin, params?.amountMax);
	if (amountRange) filter.amount = amountRange;

	const outstandingRange = buildRangeFilter(
		params?.outstandingBalanceMin,
		params?.outstandingBalanceMax,
	);
	if (outstandingRange) filter.outstandingAmount = outstandingRange;

	if (params?.onDispute !== undefined) {
		filter.hasDispute = { eq: params.onDispute };
	}

	if (params?.hasCase !== undefined) {
		filter.hasCase = { eq: params.hasCase };
	}

	return Object.keys(filter).length > 0 ? filter : undefined;
}

// --- Result type ---

export interface AddPromiseTransactionsGraphQLResult {
	transactions: Transaction[];
	pagination: PaginationResult;
	isLoading: boolean;
	error: Error | null;
	refetch: () => void;
}

// --- Hook ---

const defaultPagination: PaginationResult = createDefaultPagination(25);

interface AddPromiseTransactionsListResult {
	transactions: Transaction[];
	pagination: PaginationResult;
}

export function useAddPromiseTransactionsPaginatedGraphQL(
	customerId: number | undefined,
	excludePromiseId: number | undefined,
	params?: GetAddPromiseTransactionsParams,
): AddPromiseTransactionsGraphQLResult {
	const apiConfig = useApiConfig();

	const queryParams: GetAddPromiseTransactionsParams = {
		page: 1,
		pageSize: 25,
		sortBy: "transactionDate",
		sortOrder: "desc",
		...params,
	};

	const page = queryParams.page ?? 1;
	const pageSize = queryParams.pageSize ?? 25;

	const mockListResult: AddPromiseTransactionsListResult = {
		transactions: [],
		pagination: computeMockPagination(0, page, pageSize),
	};

	const filter = buildAddPromiseTransactionFilter(
		customerId,
		excludePromiseId,
		queryParams,
	);

	// --- List query ---
	const listQuery = useModeAwareQuery<AddPromiseTransactionsListResult, Error>(
		[
			"add-promise-transactions-graphql-list",
			customerId,
			excludePromiseId,
			queryParams,
		],
		async ({ signal }) => {
			if (!customerId) {
				return {
					transactions: [],
					pagination: computeMockPagination(0, 1, pageSize),
				};
			}

			const variables: Record<string, unknown> = {
				first: pageSize,
				sort: buildSort(
					addPromiseSortFieldMap,
					queryParams.sortBy,
					queryParams.sortOrder,
				),
				filter,
			};

			if (page > 1) {
				variables.after = pageToCursor(page);
			}

			const response =
				await graphqlQuery<AddPromiseTransactionListGraphQLResponse>(
					apiConfig,
					ADD_PROMISE_TRANSACTION_LIST_QUERY,
					variables,
					signal,
				);

			const connection =
				response.addPromiseTransactions.addPromiseTransactionList;
			const transactions = connection.edges.map((x) =>
				mapGraphQLNodeToTransaction(x.node),
			);

			const pagination: PaginationResult = {
				page,
				pageSize,
				totalItems: transactions.length,
				totalPages: 1,
				hasNextPage: connection.pageInfo.hasNextPage,
				hasPreviousPage: connection.pageInfo.hasPreviousPage,
			};

			return { transactions, pagination };
		},
		mockListResult,
		{
			enabled: !!customerId,
			staleTime: 0,
			refetchOnMount: "always",
		},
	);

	// --- Count query ---
	const countQuery = useModeAwareQuery<number, Error>(
		[
			"add-promise-transactions-graphql-count",
			customerId,
			excludePromiseId,
			filter,
		],
		async ({ signal }) => {
			if (!customerId) return 0;

			const variables: Record<string, unknown> = {};
			if (filter) {
				variables.filter = filter;
			}

			const response =
				await graphqlQuery<AddPromiseTransactionCountGraphQLResponse>(
					apiConfig,
					ADD_PROMISE_TRANSACTION_COUNT_QUERY,
					variables,
					signal,
				);

			return response.addPromiseTransactions.addPromiseTransactionCount;
		},
		0,
		{
			enabled: !!customerId,
			staleTime: 30_000,
			refetchOnMount: "always",
		},
	);

	// --- Combine results ---
	const basePagination = listQuery.data?.pagination ?? defaultPagination;
	const totalCount = countQuery.data ?? 0;
	const pagination = computePaginationWithCount(
		basePagination,
		totalCount,
		params?.pageSize,
	);

	return {
		transactions: listQuery.data?.transactions ?? [],
		pagination,
		isLoading: listQuery.isLoading,
		error: listQuery.error,
		refetch: () => {
			listQuery.refetch();
			countQuery.refetch();
		},
	};
}
